/**
 * Environment-aware API client
 * Uses direct connection in development and Cloudflare Worker proxy in production
 */

// Configuration
const API_ENDPOINTS = {
  development: '/api', // Vite proxy - will be combined with /cotas
  production: import.meta.env.VITE_API_PROXY_URL || 'https://north-platform-api-proxy.cavricks.workers.dev',
};

// Check if we're in development
const isDevelopment = import.meta.env.DEV;

/**
 * Get the base API URL based on environment
 */
export function getApiBaseUrl(): string {
  return isDevelopment ? API_ENDPOINTS.development : API_ENDPOINTS.production;
}

/**
 * Format Brazilian currency values for API
 * Converts 1234.56 to "1234,56"
 */
export function formatBrazilianNumber(value: number): string {
  return value.toFixed(2).replace('.', ',');
}

/**
 * Encode commas for URL parameters
 */
export function encodeCommas(value: string): string {
  return value.replace(/,/g, '%2C');
}

/**
 * Fetch available cotas from the API
 */
export interface FetchCotasParams {
  groupNumber: number;
  adminFee: number;
  parcela: number | string;
  seguro: boolean;
}

export interface Cota {
  value: string;
  text: string;
}

export interface CotasResponse {
  success: boolean;
  purchase_url: string;
  count: number;
  cotas: Cota[];
  error?: string;
  message?: string;
}

export interface SellServopaOptions {
  purchaseUrl?: string;
  parcela: number | string;
  /** Type-driven parcela matching: 'normal' or 'flex' (with flexPercentage). */
  parcelaType?: 'normal' | 'flex';
  /** Required when parcelaType === 'flex': 10 | 20 | 30 | 40 | 50. */
  flexPercentage?: number;
  /**
   * Debug mode: API saves screenshots/HTML at every step. The response
   * includes a `screenshots` server path. Use only for diagnosing failed
   * sales (e.g. payment-link email never arriving).
   */
  debug?: boolean;
  seguro: boolean;
  cota?: string | null;
  customer?: {
    tipo: string;
    cpf: string;
    name: string;
    birth: string;
    email: string;
    phone: string;
    renda_mensal: string;
    cep: string;
    address: string;
    number: string;
    complement: string;
    neighborhood: string;
    city: string;
    state: string;
  };
  payment?: {
    method: string;
    boletoDate?: string;
  };
}

export interface SellServopaParams {
  options: SellServopaOptions;
  attempt_id?: string; // UUID da tentativa de venda para tracking/reconciliação
  /**
   * Token da sessão Supabase de quem vende. O backend tira o vendedor (e o
   * login Servopa dele) do TOKEN — sem ele a venda volta 401.
   */
  access_token?: string;
  // Ignorados pelo backend desde 26/09/2026 (o vendedor sai do token); seguem
  // no payload só para o servidor antigo durante a janela de deploy.
  acting_user_id?: number | string;
  acting_user_email?: string;
}

export interface SellServopaResponse {
  success?: boolean;
  message?: string;
  error?: string;
  [key: string]: unknown;
}

/**
 * Erro de venda com o que a tela precisa pra decidir se oferece "Tentar de novo".
 *
 * `diagnostic` é o motivo técnico (vai pra auditoria, não pro vendedor).
 *
 * `servopaRejection`: a Servopa recusou com motivo dela (renda, seguro
 * obrigatório). Repetir com os mesmos dados dá o mesmo não.
 *
 * `podeRefazer`: é SEGURO repetir a venda. Só é verdade quando o backend
 * respondeu — resposta prova que nada ficou em voo. Quando o transporte cai
 * (524, timeout, conexão perdida) não sabemos se a venda registrou: ela pode
 * estar sendo processada neste instante, e repetir criaria contrato duplicado.
 * Por isso, ali, a orientação continua sendo conferir na aba Vendas — e o botão
 * de refazer NÃO aparece. Os dois campos são independentes: o primeiro é sobre
 * adiantar alguma coisa, o segundo é sobre não criar venda dobrada.
 */
export type SellError = Error & {
  diagnostic?: string;
  servopaRejection?: boolean;
  podeRefazer?: boolean;
};

/**
 * Fetch cotas with environment-aware endpoint
 * Uses query parameters: /cotas?groupNumber=...&adminFee=...&parcela=...&seguro=...
 * 
 * NOTE: This endpoint is currently NOT implemented on the backend (api.northbusinessteam.com.br).
 * Use fetchCotasByUrl() instead, which works with the /cotas-by-url endpoint.
 * This function is kept for future implementation or if the backend adds this endpoint.
 */
export async function fetchCotas(params: FetchCotasParams): Promise<CotasResponse> {
  const { groupNumber, adminFee, parcela, seguro } = params;

  // Format values
  const parcelaValue = typeof parcela === 'number'
    ? formatBrazilianNumber(parcela)
    : String(parcela).replace(/[^0-9,]/g, '');

  const adminFeeStr = formatBrazilianNumber(adminFee);

  // URL encode commas
  const adminFeeEncoded = encodeCommas(adminFeeStr);
  const parcelaEncoded = encodeCommas(parcelaValue);

  // Build URL with query parameters
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}/cotas?groupNumber=${groupNumber}&adminFee=${adminFeeEncoded}&parcela=${parcelaEncoded}&seguro=${seguro}`;

  console.log('Fetching cotas:', {
    environment: isDevelopment ? 'development' : 'production',
    url,
    params: {
      groupNumber,
      adminFee: adminFeeStr,
      parcela: parcelaValue,
      seguro,
    },
  });

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const raw = await response.text();
    let data: any = {};
    try {
      data = raw ? JSON.parse(raw) : {};
    } catch {
      data = {};
    }

    if (!response.ok || !data.success) {
      console.error('Cotas API error response:', {
        status: response.status,
        statusText: response.statusText,
        body: raw,
      });
      throw new Error(data.message || `Não foi possível buscar as cotas disponíveis (HTTP ${response.status}).`);
    }

    return data;
  } catch (error: any) {
    console.error('Error fetching cotas:', error);
    throw error;
  }
}

/**
 * Fetch cotas directly by purchase URL
 * Uses /cotas-by-url?purchaseUrl=...
 */
export async function fetchCotasByUrl(
  purchaseUrl: string,
  signal?: AbortSignal,
  actingUserId?: string | number | null,
  // Token da sessão: desde 26/09/2026 a rota exige login (api-server/acesso.mjs).
  accessToken?: string | null,
): Promise<CotasResponse> {
  const baseUrl = getApiBaseUrl();
  const encodedUrl = encodeURIComponent(purchaseUrl);
  // acting_user_id permite ao backend limitar a 1 busca de cotas em andamento
  // por usuário (evita empilhar sessões Browserless ao cancelar+reclicar).
  const actingParam = actingUserId != null && actingUserId !== ''
    ? `&acting_user_id=${encodeURIComponent(String(actingUserId))}`
    : '';
  const url = `${baseUrl}/cotas-by-url?purchaseUrl=${encodedUrl}${actingParam}`;

  console.log('Fetching cotas by URL:', {
    environment: isDevelopment ? 'development' : 'production',
    url,
    purchaseUrl,
  });

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      signal,
    });

    // 5xx + Cloudflare/origin errors return HTML, not JSON. Detect by status
    // first so the caller can distinguish "infra down" from "no cotas available".
    if (response.status >= 500) {
      const err: any = new Error(
        response.status === 502 || response.status === 504
          ? 'API indisponível (gateway). Tente novamente em alguns minutos.'
          : `API indisponível (HTTP ${response.status})`,
      );
      err.status = response.status;
      err.kind = 'upstream_down';
      throw err;
    }

    const data = await response.json();

    if (!response.ok || !data.success) {
      const err: any = new Error(data.message || 'Não foi possível buscar as cotas disponíveis.');
      err.status = response.status;
      err.kind = 'request_failed';
      throw err;
    }

    return data;
  } catch (error: any) {
    // Cancelamento intencional (usuário clicou "Pular espera"/Cancelar, ou o
    // efeito desmontou): não é erro — repassa em silêncio pro caller ignorar.
    if (error?.name === 'AbortError' || signal?.aborted) {
      throw error;
    }
    // Network errors (DNS fail, connection refused) → fetch throws TypeError
    if (error?.name === 'TypeError' && !error?.kind) {
      const wrapped: any = new Error('Sem conexão com a API (rede ou DNS). Tente novamente.');
      wrapped.kind = 'network_error';
      console.error('Network error fetching cotas by URL:', error);
      throw wrapped;
    }
    console.error('Error fetching cotas by URL:', error);
    throw error;
  }
}

/**
 * Cancela a busca de cotas em andamento do usuário (best-effort, fire-and-forget).
 * Usado ao sair do modal de cotas (Pular/Cancelar) pra soltar o slot do
 * Browserless na hora — o req.on('close') nem sempre chega ao EC2 atrás da
 * Cloudflare. `keepalive` deixa o POST completar mesmo se o modal/página fechar.
 */
export async function cancelCotasByUrl(
  actingUserId?: string | number | null,
  accessToken?: string | null,
): Promise<void> {
  if (actingUserId == null || actingUserId === '') return;
  const baseUrl = getApiBaseUrl();
  try {
    await fetch(`${baseUrl}/cotas-by-url/cancel`, {
      method: 'POST',
      // O servidor tira o usuário do token; acting_user_id segue no corpo só
      // para o servidor antigo durante a janela de deploy.
      headers: { 'Content-Type': 'application/json', ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
      body: JSON.stringify({ acting_user_id: actingUserId }),
      keepalive: true,
    });
  } catch {
    // best-effort: o gate também libera sozinho quando a busca antiga termina.
  }
}

const formatBrazilianValue = (value: number | string) => {
  if (typeof value === 'number') return formatBrazilianNumber(value);
  return String(value).replace(/[^0-9,]/g, '');
};

const maskValue = (value: string, visibleEnd = 2) => {
  const clean = String(value || '');
  if (clean.length <= visibleEnd) return '*'.repeat(clean.length || 1);
  return `${'*'.repeat(clean.length - visibleEnd)}${clean.slice(-visibleEnd)}`;
};

const maskEmail = (email: string) => {
  const [user, domain] = String(email || '').split('@');
  if (!domain) return maskValue(email, 0);
  return `${maskValue(user, 1)}@${domain}`;
};

const maskSellPayload = (payload: any) => {
  if (!payload?.options?.customer) return payload;
  const customer = payload.options.customer;
  return {
    ...payload,
    options: {
      ...payload.options,
      customer: {
        ...customer,
        cpf: maskValue(customer.cpf),
        phone: maskValue(customer.phone),
        email: maskEmail(customer.email),
        cep: maskValue(customer.cep),
      },
    },
  };
};

// Mensagem única para QUALQUER falha de transporte (não-resposta conclusiva do
// backend): timeout do cliente, conexão derrubada ("Load failed"), timeout do
// proxy Cloudflare (524/502/503/504 em HTML) ou job expirado no polling. Em
// todos esses casos a raspagem pode ter concluído a venda do outro lado, então
// orientamos a CONFERIR antes de refazer — o retry cego é o que duplica venda.
const SELL_TRANSPORT_FAILURE_MSG =
  'Tempo limite excedido sem confirmação da Servopa. ' +
  'A venda PODE ter sido registrada; confira na aba Vendas antes de refazer.';

/**
 * Finalize sale for Servopa (same API base as cotas)
 * Uses POST /sell with JSON payload
 */
export async function sellServopa(params: SellServopaParams): Promise<SellServopaResponse> {
  const { options, attempt_id, access_token, acting_user_id, acting_user_email } = params;
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}/sell`;

  if (!options.purchaseUrl) {
    throw new Error('options.purchaseUrl is required');
  }

  const payload: any = {
    options: {
      ...options,
      parcela: formatBrazilianValue(options.parcela),
    },
    attempt_id, // Para tracking e reconciliação
    acting_user_id,
    acting_user_email,
    // Pede o fluxo assíncrono no backend: ele responde 202 + jobId na hora e nós
    // fazemos polling. Foge do timeout de ~100s da Cloudflare (524) numa raspagem
    // longa. Backend antigo ignora a flag e responde 200 síncrono (back-compat).
    asyncJob: true,
  };

  payload.options.purchaseUrl = options.purchaseUrl;

  console.log('Selling cota:', {
    environment: isDevelopment ? 'development' : 'production',
    url,
    payload: maskSellPayload(payload),
  });

  // O POST agora retorna rápido (202 + jobId) no fluxo assíncrono. Mantemos um
  // timeout só pra cobrir o caso de backend antigo (síncrono), em que a própria
  // requisição é a operação longa.
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 120_000);
  const auth: Record<string, string> = access_token ? { Authorization: `Bearer ${access_token}` } : {};

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...auth,
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    const raw = await response.text();
    let data: any = {};
    try {
      data = raw ? JSON.parse(raw) : {};
    } catch {
      data = {};
    }

    // Fluxo assíncrono: 202 + jobId → faz polling até a venda terminar.
    if (response.status === 202 && data?.jobId) {
      clearTimeout(timeoutId); // o polling tem seu próprio limite de tempo
      return await pollSellJob(baseUrl, data.jobId, auth);
    }

    if (!response.ok || data?.success === false) {
      console.error('Sell API error response:', {
        status: response.status,
        statusText: response.statusText,
        body: raw,
      });
      // Erro estruturado do backend (ex.: "Seguro obrigatório") tem `message` —
      // mostramos a mensagem real. Sem isso, é gateway/timeout (CF 524 em HTML,
      // 502/503/504): tratamos como falha de transporte.
      const backendMessage = typeof data?.message === 'string' ? data.message.trim() : '';
      const err = new Error(backendMessage || SELL_TRANSPORT_FAILURE_MSG) as SellError;
      if (typeof data?.diagnostic === 'string' && data.diagnostic) err.diagnostic = data.diagnostic;
      err.servopaRejection = Boolean(data?.confirmation);
      // Refazer só é SEGURO quando o backend respondeu: resposta significa que
      // nada ficou em voo, a venda não vai nascer depois sozinha. Sem `message`
      // estruturada é gateway/timeout (524 em HTML) — aí a venda PODE estar
      // sendo processada agora, e repetir criaria contrato duplicado.
      // E recusa da Servopa não se refaz: com os mesmos dados dá o mesmo não.
      err.podeRefazer = Boolean(backendMessage) && !err.servopaRejection;
      throw err;
    }

    // Back-compat: backend antigo (síncrono) devolve 200 + result direto.
    return data;
  } catch (error: any) {
    console.error('Error finalizing sale:', error);
    // Timeout do cliente (AbortError) ou conexão derrubada ("Load failed" =
    // TypeError): mesma orientação — não sabemos se a venda registrou.
    if (error?.name === 'AbortError' || error instanceof TypeError) {
      throw new Error(SELL_TRANSPORT_FAILURE_MSG);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Faz polling do job de venda assíncrono (GET /job/:jobId) até concluir.
 * - 'completed' → retorna o result (mesma shape do fluxo síncrono)
 * - 'error'     → lança Error com a mensagem real do backend (ex.: seguro)
 * - 404 / sem resposta / timeout → falha de transporte (desfecho desconhecido)
 */
async function pollSellJob(
  baseUrl: string,
  jobId: string,
  // O job da venda só é lido por quem vendeu (o resultado traz contrato e boleto).
  auth: Record<string, string> = {},
): Promise<SellServopaResponse> {
  const POLL_INTERVAL_MS = 3000;
  const MAX_WAIT_MS = 10 * 60 * 1000; // a raspagem pode levar minutos
  const deadline = Date.now() + MAX_WAIT_MS;
  let networkErrors = 0;

  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

    let response: Response;
    try {
      response = await fetch(`${baseUrl}/job/${jobId}`, { method: 'GET', headers: auth });
    } catch {
      // Sem resposta (rede): tolera algumas falhas antes de desistir.
      if (++networkErrors >= 5) throw new Error(SELL_TRANSPORT_FAILURE_MSG);
      continue;
    }
    networkErrors = 0;

    // Job sumiu do servidor (expirou/reiniciou) — desfecho desconhecido.
    if (response.status === 404) {
      throw new Error(SELL_TRANSPORT_FAILURE_MSG);
    }

    let job: any = {};
    try {
      const raw = await response.text();
      job = raw ? JSON.parse(raw) : {};
    } catch {
      job = {};
    }

    if (job.status === 'completed') {
      return job.result as SellServopaResponse;
    }
    if (job.status === 'error') {
      // `message` é a genérica exibida ao vendedor; `diagnostic` é o detalhe
      // técnico (challenge/credencial) que só vai pra auditoria — anexado ao erro.
      const err = new Error(job.message || 'Não foi possível finalizar a venda.') as SellError;
      if (typeof job.diagnostic === 'string' && job.diagnostic) err.diagnostic = job.diagnostic;
      err.servopaRejection = Boolean(job.confirmation);
      // O job terminou com status 'error': o servidor chegou ao fim e contou o
      // que houve, então não há venda em voo — refazer é seguro (salvo recusa).
      err.podeRefazer = !err.servopaRejection;
      throw err;
    }
    // job.status === 'pending' → continua o loop
  }

  // Estourou o tempo máximo de espera no polling.
  throw new Error(SELL_TRANSPORT_FAILURE_MSG);
}

/**
 * Generic API fetch wrapper with environment awareness
 */
export async function apiFetch<T = any>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `API error: ${response.status}`);
  }

  return response.json();
}
